import {
  BidStatus,
  type PortalMyBidDto,
  PortalAwardOutcomeDto,
  PortalLifecycleStatus,
} from "../../autogen/bff-api";
import { displayDateWithTime } from "../../common/formatting/displayDateWithTime";
import { isDesignContest } from "../sourcing-events/buyer/wizard/utils/procedure";

const AWARD_DECIDED: PortalLifecycleStatus[] = [PortalLifecycleStatus.Awarded, PortalLifecycleStatus.Concluded];

export const isCompetitionOpen = (bid: PortalMyBidDto): boolean =>
  bid.event.lifecycle.status === PortalLifecycleStatus.Open && !bid.event.frameworkAgreementNoLongerActive;

export const awardDecisionReached = (
  lifecycle: PortalLifecycleStatus,
  awardOutcome: PortalAwardOutcomeDto | null | undefined,
): boolean => AWARD_DECIDED.includes(lifecycle) || awardOutcome === PortalAwardOutcomeDto.NotSelected;

export const myBidTagProps = (bid: PortalMyBidDto) => ({
  status: bid.awardOutcome === PortalAwardOutcomeDto.Winner ? BidStatus.Awarded : bid.status,
  awarded: awardDecisionReached(bid.event.lifecycle.status, bid.awardOutcome),
  biddingClosed: !isCompetitionOpen(bid),
  standstillUntil: bid.standstillExpiresAt,
  notContinued: bid.event.lifecycle.status === PortalLifecycleStatus.Cancelled,
  designContest: isDesignContest(bid.event.procedureType),
});

export const myBidDeadlineText = (bid: PortalMyBidDto, locale: string): string =>
  bid.event.bidSubmissionDeadline ? displayDateWithTime(bid.event.bidSubmissionDeadline, null, locale) : "—";

const deadlineOf = (bid: PortalMyBidDto): number => new Date(bid.event.bidSubmissionDeadline).getTime();

export const sortByUrgency = (bids: PortalMyBidDto[]): PortalMyBidDto[] => {
  const open = bids.filter(isCompetitionOpen).sort((a, b) => deadlineOf(a) - deadlineOf(b));
  const closed = bids.filter((bid) => !isCompetitionOpen(bid)).sort((a, b) => deadlineOf(b) - deadlineOf(a));
  return [...open, ...closed];
};

export const lifecycleFilterOf = (bid: PortalMyBidDto): PortalLifecycleStatus | null =>
  bid.event.lifecycle.status === PortalLifecycleStatus.Open && !isCompetitionOpen(bid) ? null : bid.event.lifecycle.status;

export const countByLifecycle = (bids: PortalMyBidDto[]): Partial<Record<PortalLifecycleStatus, number>> => {
  const counts: Partial<Record<PortalLifecycleStatus, number>> = {};
  for (const bid of bids) {
    const status = lifecycleFilterOf(bid);
    if (status !== null) counts[status] = (counts[status] ?? 0) + 1;
  }
  return counts;
};
